import { CompanionCardHeader } from '@/components/companion/cards/CompanionCardHeader';
import { CompanionCardShell, EvidenceLine } from '@/components/companion/cards/CompanionCardShell';
import {
  COMPANION_DAY_MONTH,
  formatCompanionDate,
} from '@/components/companion/feed/companion-formatters';
import { Box, HStack, Link, Stack, Text } from '@chakra-ui/react';
import type { CompanionActionDto, CompanionMentionNotableEvidence } from '@fluenceur/types';
import { ArrowUpRight } from 'iconoir-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The notable-mention card, as the companion panel shows it.
 *
 * A presenter and nothing else. Every line comes from the evidence the server
 * froze at publication: which readings qualified the author, and the figures
 * behind them when there are any. A figure that was never captured is left out
 * rather than shown as a zero — the brand is about to write to a person on the
 * strength of these lines, and each one has to be checkable.
 *
 * The call to action says what it does. It never claims the advert is running,
 * because asking a creator for permission is not the same as advertising.
 */

interface MentionNotableCardProps {
  action: CompanionActionDto & { evidence: CompanionMentionNotableEvidence };
  /** Opens the workflow the card names. Wired by the panel, never by this card. */
  onOpenCta: () => void;
  /** Starts the dismissal choice. Absent while the panel does not offer it. */
  onDismiss?: () => void;
  isCtaPending?: boolean;
  /** The workflow has no adapter in this build; the button is shown inert. */
  isCtaDisabled?: boolean;
  /** Fills the panel edge to edge: no frame of its own, the panel is the frame. */
  flat?: boolean;
  /** The host's own controls, rendered at the end of the action row. */
  footerEnd?: ReactNode;
}

const formatCount = (value: number, language: string): string =>
  new Intl.NumberFormat(language).format(value);

export const MentionNotableCard = ({
  action,
  onOpenCta,
  onDismiss,
  isCtaPending = false,
  isCtaDisabled = false,
  flat = false,
  footerEnd,
}: MentionNotableCardProps) => {
  const { t, i18n } = useTranslation();
  const { evidence } = action;
  const language = i18n.language;

  const metaParts = [
    evidence.mediaSource
      ? t(`companion.cards.mentionNotable.mediaSource.${evidence.mediaSource}`, {
          defaultValue: evidence.mediaSource,
        })
      : null,
    // The day and the month: a mention this card carries is at most 48 h old,
    // so the year would only add noise.
    formatCompanionDate(evidence.publishedAt, language, COMPANION_DAY_MONTH),
    evidence.metrics?.views !== null && evidence.metrics?.views !== undefined
      ? t('companion.cards.mentionNotable.views', {
          count: evidence.metrics.views,
          value: formatCount(evidence.metrics.views, language),
        })
      : null,
    evidence.metrics?.comments !== null && evidence.metrics?.comments !== undefined
      ? t('companion.cards.mentionNotable.comments', {
          count: evidence.metrics.comments,
          value: formatCount(evidence.metrics.comments, language),
        })
      : null,
  ].filter((part): part is string => part !== null);

  const excerptBody = (
    <Stack gap="4px">
      {evidence.excerpt ? (
        <Text fontSize="13px" color="ink" lineHeight="1.5">
          {evidence.excerpt}
        </Text>
      ) : null}
      <HStack gap="4px" align="center">
        <Text fontSize="12px" color="inkSubtle" lineHeight="1.4">
          {metaParts.join(' · ')}
        </Text>
        {evidence.permalink ? <ArrowUpRight width={12} height={12} color="inherit" /> : null}
      </HStack>
    </Stack>
  );

  return (
    <CompanionCardShell
      testId="companion-mention-notable-card"
      dataAttrs={{ 'data-mention-id': evidence.mentionId }}
      flat={flat}
      ctaLabel={action.ctaLabel}
      onOpenCta={onOpenCta}
      onDismiss={onDismiss}
      isCtaPending={isCtaPending}
      isCtaDisabled={isCtaDisabled}
      footerEnd={footerEnd}
    >
      <CompanionCardHeader title={action.title} reason={action.reason} />

      {evidence.permalink ? (
        <Link
          href={evidence.permalink}
          target="_blank"
          rel="noopener noreferrer"
          pl="14px"
          py="11px"
          borderLeftWidth="2px"
          borderColor="ink"
          color="inkSubtle"
          _hover={{ textDecoration: 'none', color: 'ink' }}
          data-testid="mention-notable-excerpt"
        >
          {excerptBody}
        </Link>
      ) : (
        <Box
          pl="14px"
          py="11px"
          borderLeftWidth="2px"
          borderColor="ink"
          data-testid="mention-notable-excerpt"
        >
          {excerptBody}
        </Box>
      )}

      <Stack gap="2px">
        {evidence.qualifiedBy.includes('trend') ? (
          <EvidenceLine>
            {evidence.trendRatio === null
              ? t('companion.cards.mentionNotable.signal.trend')
              : t('companion.cards.mentionNotable.signal.trendWithValue', {
                  ratio: evidence.trendRatio.toFixed(1),
                })}
          </EvidenceLine>
        ) : null}
        {evidence.qualifiedBy.includes('score') ? (
          <EvidenceLine>
            {evidence.matchScore === null
              ? t('companion.cards.mentionNotable.signal.score')
              : t('companion.cards.mentionNotable.signal.scoreWithValue', {
                  score: evidence.matchScore,
                })}
          </EvidenceLine>
        ) : null}
        {evidence.qualifiedBy.includes('frequency') ? (
          <EvidenceLine>
            {t('companion.cards.mentionNotable.signal.frequency', {
              count: evidence.mentionsLast30d,
            })}
          </EvidenceLine>
        ) : null}
      </Stack>
    </CompanionCardShell>
  );
};
